/*
  IMPORTANT: this file cannot reference any modules that are related to react & redux
  this is because service-worker runs under a different scope and cannot use those modules
*/

import * as logger from 'logger';
import { EntryMode } from 'types/entry-mode';
import { Epc } from 'types/epc';
import { Geot } from 'types/geot-details';
import { getDatabase } from 'database';
import { getI18n } from './i18n-service';
import { Guid } from 'types/guid';
import { NmvsActionType } from 'types/nmvs-action-type';
import { NmvsMenuItem } from 'types/nmvs-menu-item';
import { OperationItem } from 'types/operation-item';
import { translations } from 'types/translations';

export function buildPendingItem(epc: Epc, geot: Geot, entryMode: EntryMode, operationType: NmvsMenuItem, countryCode: string,
                                 language: string, sessionToken: string): OperationItem {
  // add to database
  const operationItem: OperationItem = {
    clientTransactionId: Guid.newGuid(),
    epc: epc,
    geot: geot,
    countryCode,
    language,
    operation: operationType.nmvsOperation,
    sessionToken,
    scanDatetime: new Date().toISOString(),
    serverRequestStarted: '',
    isProcessing: false,
    hasError: false,
    hasAlert: false,
    alertMessage: '',
    entryMode,
    nmvsResponseTime: 0,
    optelResponseTime: 0,
    totalResponseTime: 0
  };
  
  return operationItem;
}

export const getItemAlertMessage = (errorKey: string, alertReason: string) => {
  return `${errorKey} ${alertReason}`;
};

export async function addOrUpdateCompletedItem(key: string, item: OperationItem): Promise<void> {
  // add item to completed index or update if it exists
  const db = await getDatabase();
  if (await db.read(db.config.indexes.Complete, key)) {
    await db.put(db.config.indexes.Complete, item, key);
  } else {
    await db.add(db.config.indexes.Complete, item, key);
  }

  // remove item from pending index
  await db.remove(db.config.indexes.Pending, key);
}

export async function addPendingItem(key: string, item: OperationItem): Promise<void> {
  try {
    // add to database
    const db = await getDatabase();
    await db.add(db.config.indexes.Pending, item, key);
  } 
  catch (error) {
    logger.error(error, 'itemService.addPendingItem');
    throw new Error(error);
  }
}

export async function updateItemProcessing(key: string, item: OperationItem, isProcessing: boolean): Promise<void> {
  item.isProcessing = isProcessing;
  item.serverRequestStarted = new Date().toISOString();
  const db = await getDatabase();
  await db.put(db.config.indexes.Pending, item, key);
}

export async function updateItemToError(key: string, item: OperationItem) {
  const i18n = getI18n();
  item.hasError = true;
  item.isProcessing = false;
  item.epc.status = i18n.t(translations.Error);
  await addOrUpdateCompletedItem(key, item);
}

export async function removeCompletedItem(key: string) {
  const db = await getDatabase();
  await db.remove(db.config.indexes.Complete, key);
}

export async function removeCompletedItems(actionType: NmvsActionType) {
  const db = await getDatabase();
  const completeRows = (await db.readAll(db.config.indexes.Complete)) as OperationItem[];
  const rowsToClear = completeRows.filter(v => v.operation === actionType);
  for (const rowToClear of rowsToClear) {
    await db.remove(db.config.indexes.Complete, rowToClear.clientTransactionId.toString());
  }
}

export async function retryCompletedItem(item: OperationItem) {
  try {
    const db = await getDatabase();
    if (await db.read(db.config.indexes.Complete, item.clientTransactionId)) {
      await db.remove(db.config.indexes.Complete, item.clientTransactionId);
    }
    await db.add(db.config.indexes.Pending, item, item.clientTransactionId);
  } 
  catch (error) {
    logger.error(error, 'itemService.retryCompletedItem');
    throw new Error(error);
  }
}

export async function getPendingItem(clientTransactionId: Guid) {
  const db = await getDatabase();
  let operationItem: OperationItem = await db.read(db.config.indexes.Pending, clientTransactionId.toString()) as OperationItem;
  return operationItem;
}

export async function getOperationItem(clientTransactionId: Guid) {
  const db = await getDatabase();
  let operationItem: OperationItem = await db.read(db.config.indexes.Complete, clientTransactionId.toString()) as OperationItem;
  if (!operationItem) {
    operationItem = await db.read(db.config.indexes.Pending, clientTransactionId.toString()) as OperationItem;
  }
  return operationItem;
}


// WEBPACK FOOTER //
// ./src/services/item-service.ts